import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "@/components/ui/input";
import { Seo } from "../components/Seo";
import { Logo } from "../components/Logo";
import { Search, Star, X, Play, Film } from "lucide-react";

const FILTERS = [
    { key: "all", label: "Trending" },
    { key: "movie", label: "Movies" },
    { key: "tv", label: "TV" },
];

export default function Watch() {
    const { user } = useAuth();
    const [filter, setFilter] = useState("all");
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const loadTrending = useCallback(async (media) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/watch/trending?media=${media}`);
            setItems(data.results);
        } catch { setItems([]); }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!searching) loadTrending(filter);
    }, [filter, searching, loadTrending]);

    const runSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) { setSearching(false); return; }
        setSearching(true);
        setLoading(true);
        try {
            const { data } = await api.get(`/watch/search?q=${encodeURIComponent(query)}`);
            setItems(data.results);
        } catch { setItems([]); }
        setLoading(false);
    };

    const clearSearch = () => { setQuery(""); setSearching(false); };

    return (
        <div className="min-h-screen bg-cream" data-testid="watch-page">
            <Seo title="Watch — Trending Movies & TV on Flex Line" description="Browse trending movies and TV shows, search titles, and watch trailers. Powered by TMDB." path="/watch" image="https://flexlines.store/og-default.png" />
            <header className="border-b border-[#DDD6FE] bg-paper sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="watch-logo-link"><Logo /></Link>
                    <Link to={user ? "/dashboard" : "/"} className="text-sm font-mono text-emerald-deep link-underline" data-testid="watch-back-link">
                        {user ? "Back to app →" : "Get your number →"}
                    </Link>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-4 lg:px-8 py-10 space-y-8">

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5">
                        <Film className="w-3.5 h-3.5" /> Flex Line Watch
                    </p>
                    <h1 className="font-display text-4xl font-light tracking-tight">
                        What&apos;s <span className="font-serif-italic">trending.</span>
                    </h1>
                    <p className="text-zinc-600 mt-2 text-sm">Browse movies & TV, search any title, and play the trailer.</p>
                </div>
                <form onSubmit={runSearch} className="relative w-full lg:w-80" data-testid="watch-search-form">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search movies & TV…"
                        data-testid="watch-search-input" className="pl-9 pr-9 rounded-sm border-[#DDD6FE] h-10" />
                    {searching && (
                        <button type="button" onClick={clearSearch} data-testid="watch-search-clear"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </form>
            </div>

            {!searching && (
                <div className="flex gap-2" data-testid="watch-filters">
                    {FILTERS.map((f) => (
                        <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`watch-filter-${f.key}`}
                            className={`px-4 h-9 rounded-sm text-sm border transition-colors ${filter === f.key ? "bg-[#4C1D95] text-white border-[#4C1D95]" : "bg-white border-[#DDD6FE] hover:bg-cream"}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => <div key={i} className="aspect-[2/3] rounded-sm bg-[#F3E8FF] animate-pulse" />)}
                </div>
            ) : items.length === 0 ? (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-12 text-center text-sm text-zinc-500" data-testid="watch-empty">
                    No titles found{searching ? ` for “${query}”` : ""}.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="watch-grid">
                    {items.map((m) => (
                        <button key={`${m.media_type}-${m.id}`} onClick={() => setSelected(m)} data-testid={`watch-card-${m.id}`}
                            className="group text-left">
                            <div className="aspect-[2/3] rounded-sm overflow-hidden border border-[#DDD6FE] bg-[#F3E8FF] relative">
                                {m.poster
                                    ? <img src={m.poster} alt={m.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                    : <div className="w-full h-full flex items-center justify-center text-zinc-400"><Film className="w-8 h-8" /></div>}
                                <span className="absolute top-1.5 left-1.5 font-mono text-[9px] uppercase bg-black/60 text-white px-1.5 py-0.5 rounded-sm">{m.media_type === "tv" ? "TV" : "Movie"}</span>
                            </div>
                            <div className="mt-2">
                                <p className="text-sm font-medium leading-snug line-clamp-1">{m.title}</p>
                                <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5">
                                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> {m.rating || "—"} {m.year && `· ${m.year}`}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {selected && <DetailModal item={selected} onClose={() => setSelected(null)} />}
            </main>
        </div>
    );
}

function DetailModal({ item, onClose }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        api.get(`/watch/${item.media_type}/${item.id}`).then(({ data }) => setDetail(data)).catch(() => setDetail(item));
    }, [item]);
    const d = detail || item;
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true" data-testid="watch-detail-modal">
            <div className="bg-paper rounded-sm border border-[#DDD6FE] max-w-3xl w-full my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {d.backdrop && (
                    <div className="relative aspect-video bg-black">
                        <img src={d.backdrop} alt={d.title} className="w-full h-full object-cover opacity-80" />
                        <button onClick={onClose} data-testid="watch-detail-close" className="absolute top-3 right-3 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"><X className="w-4 h-4" /></button>
                    </div>
                )}
                <div className="p-6">
                    {!d.backdrop && <button onClick={onClose} className="float-right text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>}
                    <h2 className="font-display text-3xl">{d.title} {d.year && <span className="text-zinc-400 font-light">({d.year})</span>}</h2>
                    {detail?.tagline && <p className="text-sm font-serif-italic text-zinc-500 mt-1">{detail.tagline}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-zinc-600">
                        <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /> {d.rating || "—"}/10</span>
                        {detail?.runtime ? <span>· {detail.runtime} min</span> : null}
                        {(detail?.genres || []).slice(0, 3).map((g) => <span key={g} className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-[#DDD6FE] rounded-sm">{g}</span>)}
                    </div>
                    <p className="text-sm text-zinc-700 mt-4 leading-relaxed">{d.overview || "No description available."}</p>

                    {detail?.trailer_key ? (
                        <div className="mt-5 aspect-video rounded-sm overflow-hidden border border-[#DDD6FE]">
                            <iframe title="trailer" data-testid="watch-trailer-iframe" className="w-full h-full"
                                src={`https://www.youtube.com/embed/${detail.trailer_key}`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                    ) : detail && (
                        <p className="mt-4 text-sm text-zinc-400 flex items-center gap-2"><Play className="w-4 h-4" /> No trailer available.</p>
                    )}

                    {(detail?.cast || []).length > 0 && (
                        <div className="mt-6">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Cast</p>
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {detail.cast.map((c, i) => (
                                    <div key={i} className="shrink-0 w-20 text-center">
                                        <div className="w-20 h-20 rounded-sm overflow-hidden bg-[#F3E8FF] border border-[#DDD6FE]">
                                            {c.photo ? <img src={c.photo} alt={c.name} className="w-full h-full object-cover" /> : null}
                                        </div>
                                        <p className="text-xs font-medium mt-1 line-clamp-1">{c.name}</p>
                                        <p className="text-[10px] text-zinc-500 line-clamp-1">{c.character}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
